import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Home, CheckCircle, XCircle, Loader2, LogIn } from 'lucide-react';
import { householdsApi } from '../api/households';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/Button';

type InviteState = 'loading' | 'success' | 'error' | 'needs-login';

// =============================================
// Accept Invite Page
// =============================================
export const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [state, setState] = useState<InviteState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage('Kein Einladungstoken gefunden.');
      return;
    }

    if (!isAuthenticated) {
      setState('needs-login');
      return;
    }

    householdsApi
      .acceptInvite(token)
      .then(() => {
        setState('success');
      })
      .catch((err: unknown) => {
        setState('error');
        const apiMessage =
          err instanceof Error
            ? err.message
            : 'Die Einladung konnte nicht angenommen werden. Sie ist möglicherweise abgelaufen oder ungültig.';
        setErrorMessage(apiMessage);
      });
  }, [token, isAuthenticated]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-[#13131f] px-4">
      <div className="max-w-md w-full bg-white dark:bg-[#1e1e2e] rounded-2xl shadow-sm
                      border border-neutral-200 dark:border-indigo-500/10 p-8 text-center">

        {state === 'loading' && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
            <h1 className="text-xl font-bold text-neutral-800 dark:text-white mb-2">
              Einladung wird verarbeitet...
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Bitte warte einen Moment.
            </p>
          </>
        )}

        {state === 'needs-login' && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-primary-500" />
            </div>
            <h1 className="text-xl font-bold text-neutral-800 dark:text-white mb-2">
              Anmeldung erforderlich
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
              Du musst dich zuerst anmelden oder registrieren, um die Einladung anzunehmen.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate(`/login?redirect=/invite/${token}`)}>
                <LogIn className="w-4 h-4 mr-1.5" />
                Anmelden
              </Button>
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-success-50 dark:bg-success-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success-500" />
            </div>
            <h1 className="text-xl font-bold text-neutral-800 dark:text-white mb-2">
              Einladung angenommen!
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
              Du bist jetzt Mitglied des Haushalts. Du kannst jetzt gemeinsame
              Kassenzettel einsehen und verwalten.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-1.5" />
                Zur Startseite
              </Button>
              <Button variant="outline" onClick={() => navigate('/settings')}>
                Haushalt verwalten
              </Button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-error-50 dark:bg-error-500/10 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-error-500" />
            </div>
            <h1 className="text-xl font-bold text-neutral-800 dark:text-white mb-2">
              Einladung ungültig
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
              {errorMessage || 'Die Einladung konnte nicht angenommen werden.'}
            </p>
            <Button onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-1.5" />
              Zur Startseite
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
